/**
 * Hand-built SVG/HTML charts (no chart library, as in the sibling repos).
 *
 * Conventions (dataviz skill): series colours come from the validated
 * categorical palette in fixed order (--series-1…8), keyed by the series'
 * position in the payload so a colour follows its entity; 2px lines; bars
 * with 4px rounded data-ends and a 2px gap; recessive grid; a legend for 2+
 * series; a hover layer on every plot; text always in ink tokens, never in
 * series colours. One y-axis only. A point still in progress (today, or the
 * current month) is drawn dashed / faded and labelled "partial" (audit MET-12).
 */

import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fmt, fmtAxis, fmtCompact, fmtX } from "../lib/format.js";
import { niceMax, seriesColor } from "../lib/chartScale.js";
import { MEASURE_HINTS, MEASURE_LABELS, isPartialPoint } from "../lib/scope.js";
import { toneColor } from "../lib/tones.js";
import { useEmptyContext } from "../lib/emptyContext.js";
import { istToday } from "../../shared/dates.js";
import { Button, DeltaBadge } from "./primitives.jsx";
import { Icon } from "./ui/index.js";

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function ticks(max, count = 4) {
  return Array.from({ length: count + 1 }, (_, i) => (max / count) * i);
}

/**
 * Which legend items are switched off. Keys that leave the data are ignored,
 * and the last visible item cannot be switched off (a chart is never empty).
 */
function useToggles(keys) {
  const [off, setOff] = useState(() => new Set());
  const hidden = useMemo(() => new Set(keys.filter((k) => off.has(k))), [keys, off]);
  const toggle = (key) =>
    setOff((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (keys.filter((k) => !next.has(k)).length > 1) next.add(key);
      return next;
    });
  return { hidden, toggle, reset: () => setOff(new Set()) };
}

/** A legend item: a button that shows or hides its series. */
function LegendItem({ label, color, off, onToggle, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={!off}
      title={off ? `Show ${label}` : `Hide ${label}`}
      className={`flex cursor-pointer items-center gap-1.5 rounded-[3px] text-left hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] ${off ? "text-ink-3 line-through" : ""} ${className}`}
    >
      <span
        className="inline-block size-2 shrink-0 rounded-[2px]"
        style={off ? { boxShadow: `inset 0 0 0 1.5px ${color}` } : { background: color }}
        aria-hidden
      />
      {children ?? label}
    </button>
  );
}

function ShowAll({ toggles }) {
  if (!toggles.hidden.size) return null;
  return (
    <li>
      <button type="button" onClick={toggles.reset} className="text-ink-3 underline-offset-2 hover:text-ink hover:underline">
        Show all
      </button>
    </li>
  );
}

/** Series legend; click an item to hide or show it. Colours stay with their series. */
export function Legend({ series, toggles }) {
  if (series.length < 2) return null;
  return (
    <ul className="mb-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11px] text-ink-2">
      {series.map((s, i) => (
        <li key={s.key}>
          <LegendItem label={s.label} color={seriesColor(i)} off={toggles?.hidden.has(s.key)} onToggle={() => toggles?.toggle(s.key)} />
        </li>
      ))}
      {toggles && <ShowAll toggles={toggles} />}
    </ul>
  );
}

/** Series still switched on, each with its original index (its colour). */
function visibleSeries(series, toggles) {
  return series.map((s, i) => ({ ...s, ci: i })).filter((s) => !toggles.hidden.has(s.key));
}

function Tooltip({ x, y, width, children }) {
  if (x === null) return null;
  const left = Math.min(Math.max(x + 12, 0), Math.max(0, width - 200));
  return (
    <div
      className="pointer-events-none absolute z-10 min-w-36 max-w-60 rounded-control border border-line bg-surface-raised px-3 py-2 text-[11px] shadow-pop"
      style={{ left, top: Math.max(0, y - 8) }}
      role="status"
    >
      {children}
    </div>
  );
}

/** Index of the last point when it is still in progress, else -1. */
function partialIndex(x, xFormat) {
  const last = x.length - 1;
  return last >= 0 && isPartialPoint(x[last], xFormat, istToday()) ? last : -1;
}

const tooltipTitle = (x, i, xFormat, partial) => `${fmtX(x[i], xFormat)}${i === partial ? " · partial" : ""}`;

function TooltipRows({ title, rows, format }) {
  return (
    <>
      <div className="mb-1 font-medium text-ink">{title}</div>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between gap-3 text-ink-2">
          <span className="flex items-center gap-1.5 truncate">
            {r.color && <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: r.color }} />}
            <span className="truncate">{r.label}</span>
          </span>
          <span className="tabular font-medium text-ink">{fmt(r.value, format)}</span>
        </div>
      ))}
    </>
  );
}

const M = { top: 10, right: 12, bottom: 26 };

/** Pick tick indexes so labels never collide. */
function xTickIndexes(n, plotWidth, minGap = 64) {
  if (n <= 1) return [0];
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotWidth / minGap))));
  const out = [];
  for (let i = 0; i < n; i += every) out.push(i);
  return out;
}

export function LineChart({ data, height = 240 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const { x, series: all, format, xFormat } = data;
  const toggles = useToggles(all.map((s) => s.key));
  const series = visibleSeries(all, toggles);
  const partial = partialIndex(x, xFormat);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const left = 8 + Math.max(...ticks(max).map((t) => fmtCompact(t).length)) * 7;
  const plotW = Math.max(10, width - left - M.right);
  const plotH = height - M.top - M.bottom;
  const px = (i) => left + (x.length <= 1 ? plotW / 2 : (i * plotW) / (x.length - 1));
  const py = (v) => M.top + plotH - (v / max) * plotH;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = e.clientX - rect.left - left;
    const i = x.length <= 1 ? 0 : Math.round((rel / plotW) * (x.length - 1));
    setHover(Math.min(x.length - 1, Math.max(0, i)));
  };

  return (
    <div>
      <Legend series={all} toggles={toggles} />
      <div ref={ref} className="relative" style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label="Line chart" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            {ticks(max).map((t) => (
              <g key={t}>
                <line x1={left} x2={left + plotW} y1={py(t)} y2={py(t)} stroke="var(--grid)" strokeWidth={1} />
                <text x={left - 6} y={py(t)} dy="0.32em" textAnchor="end" fontSize={10} fill="var(--text-muted)" className="tabular">
                  {fmtCompact(t)}
                </text>
              </g>
            ))}
            {xTickIndexes(x.length, plotW).map((i) => (
              <text key={i} x={px(i)} y={height - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
                {fmtAxis(x[i], xFormat)}
              </text>
            ))}
            {series.map((s) => {
              // The in-progress last point gets its own dashed segment.
              const solid = partial > 0 ? s.values.slice(0, partial) : s.values;
              return (
                <g key={s.key}>
                  <path
                    d={solid.map((v, i) => `${i ? "L" : "M"}${px(i)},${py(v)}`).join("")}
                    fill="none"
                    stroke={seriesColor(s.ci)}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {partial > 0 && (
                    <path
                      d={`M${px(partial - 1)},${py(s.values[partial - 1])}L${px(partial)},${py(s.values[partial])}`}
                      fill="none"
                      stroke={seriesColor(s.ci)}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      strokeLinecap="round"
                    />
                  )}
                </g>
              );
            })}
            {x.length <= 1 &&
              series.map((s) => <circle key={s.key} cx={px(0)} cy={py(s.values[0] ?? 0)} r={4} fill={seriesColor(s.ci)} />)}
            {hover !== null && (
              <g>
                <line x1={px(hover)} x2={px(hover)} y1={M.top} y2={M.top + plotH} stroke="var(--text-muted)" strokeDasharray="3 3" />
                {series.map((s) => (
                  <circle key={s.key} cx={px(hover)} cy={py(s.values[hover])} r={4.5} fill={seriesColor(s.ci)} stroke="var(--surface-1)" strokeWidth={2} />
                ))}
              </g>
            )}
            <rect x={left} y={M.top} width={plotW} height={plotH} fill="transparent" />
          </svg>
        )}
        {hover !== null && (
          <Tooltip x={px(hover)} y={M.top} width={width}>
            <TooltipRows
              title={tooltipTitle(x, hover, xFormat, partial)}
              format={format}
              rows={series
                .map((s) => ({ key: s.key, label: s.label, value: s.values[hover], color: seriesColor(s.ci) }))
                .sort((a, b) => b.value - a.value)}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/** Vertical (grouped) columns. */
export function ColumnChart({ data, height = 240 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const { x, series: all, format, xFormat } = data;
  const toggles = useToggles(all.map((s) => s.key));
  const series = visibleSeries(all, toggles);
  const partial = partialIndex(x, xFormat);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const left = 8 + Math.max(...ticks(max).map((t) => fmtCompact(t).length)) * 7;
  const plotW = Math.max(10, width - left - M.right);
  const plotH = height - M.top - M.bottom;
  const band = plotW / Math.max(1, x.length);
  const groupW = Math.min(band * 0.72, 18 * series.length + 2 * (series.length - 1));
  const barW = Math.max(2, (groupW - 2 * (series.length - 1)) / series.length);
  const py = (v) => M.top + plotH - (v / max) * plotH;
  const showLabels = series.length === 1 && x.length <= 14 && band > 34;

  return (
    <div>
      <Legend series={all} toggles={toggles} />
      <div ref={ref} className="relative" style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label="Column chart" onMouseLeave={() => setHover(null)}>
            {ticks(max).map((t) => (
              <g key={t}>
                <line x1={left} x2={left + plotW} y1={py(t)} y2={py(t)} stroke="var(--grid)" />
                <text x={left - 6} y={py(t)} dy="0.32em" textAnchor="end" fontSize={10} fill="var(--text-muted)" className="tabular">
                  {fmtCompact(t)}
                </text>
              </g>
            ))}
            {x.map((xv, i) => {
              const gx = left + i * band + (band - groupW) / 2;
              return (
                <g key={xv} onMouseEnter={() => setHover(i)}>
                  <rect x={left + i * band} y={M.top} width={band} height={plotH} fill={hover === i ? "var(--surface-2)" : "transparent"} />
                  {series.map((s, si) => {
                    const v = s.values[i] ?? 0;
                    const h = Math.max(0, M.top + plotH - py(v));
                    const bx = gx + si * (barW + 2);
                    const r = Math.min(4, barW / 2, h);
                    const top = M.top + plotH - h;
                    return (
                      <path
                        key={s.key}
                        d={`M${bx},${M.top + plotH}V${top + r}Q${bx},${top} ${bx + r},${top}H${bx + barW - r}Q${bx + barW},${top} ${bx + barW},${top + r}V${M.top + plotH}Z`}
                        fill={seriesColor(s.ci)}
                        fillOpacity={i === partial ? 0.45 : 1}
                      />
                    );
                  })}
                  {showLabels && (
                    <text x={gx + groupW / 2} y={py(series[0].values[i] ?? 0) - 5} textAnchor="middle" fontSize={10} fill="var(--text-secondary)" className="tabular">
                      {fmtCompact(series[0].values[i] ?? 0)}
                    </text>
                  )}
                </g>
              );
            })}
            {xTickIndexes(x.length, plotW, 44).map((i) => (
              <text key={i} x={left + i * band + band / 2} y={height - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
                {fmtAxis(x[i], xFormat)}
              </text>
            ))}
            <line x1={left} x2={left + plotW} y1={M.top + plotH} y2={M.top + plotH} stroke="var(--border)" />
          </svg>
        )}
        {hover !== null && (
          <Tooltip x={left + hover * band + band / 2} y={M.top} width={width}>
            <TooltipRows
              title={tooltipTitle(x, hover, xFormat, partial)}
              format={format}
              rows={series.map((s) => ({ key: s.key, label: s.label, value: s.values[hover], color: all.length > 1 ? seriesColor(s.ci) : null }))}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/** A `stack` key's colour: named values in fixed order, then neutral "Other" and "Unknown". */
const stackColor = (key, i) => (key === "Other" ? "var(--text-muted)" : key === "Unknown" ? "var(--border-strong)" : seriesColor(i));

/**
 * Ranked bars, each split into segments (`data.stack.keys`, `item.parts`).
 * Segments keep their key's colour on every bar; a 2px surface gap separates
 * them. Hovering a row lists its breakup with shares.
 */
function StackedBarList({ data, max: maxRows }) {
  const items = data.items.slice(0, maxRows);
  const top = Math.max(1, ...items.map((i) => i.value));
  const total = data.items.reduce((s, i) => s + i.value, 0);
  const keys = data.stack.keys.map((k, i) => ({ ...k, color: stackColor(k.key, i) }));
  const [hover, setHover] = useState(null);
  if (!items.length) return <Empty />;
  return (
    <div>
      <ul className="mb-3 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11.5px]" aria-label={`${data.stack.title} colours`}>
        <li className="text-[10px] font-semibold tracking-[0.08em] text-ink-3 uppercase">{data.stack.title}</li>
        {keys.map((k) => (
          <li key={k.key} className="flex items-center gap-1.5 text-ink-2">
            <span aria-hidden className="size-2 rounded-[2px]" style={{ background: k.color }} />
            {k.label}
          </li>
        ))}
      </ul>
      <ul className="space-y-2">
        {items.map((it) => {
          const breakup = keys.filter((k) => it.parts[k.key] > 0);
          const share = (n) => (it.value ? `${Math.round((n / it.value) * 100)}%` : "");
          return (
            <li
              key={it.label}
              className="relative grid grid-cols-[minmax(0,44%)_1fr_auto] items-center gap-3 text-[12px]"
              onMouseEnter={() => setHover(it.label)}
              onMouseLeave={() => setHover(null)}
              aria-label={`${it.label}: ${fmt(it.value, data.format)}; ${breakup.map((k) => `${k.label} ${fmt(it.parts[k.key], data.format)}`).join(", ")}`}
            >
              <span className="truncate text-ink">{it.label}</span>
              <span className="flex h-2.5 gap-[2px]" style={{ width: `${(it.value / top) * 100}%` }}>
                {breakup.map((k) => (
                  <span key={k.key} className="h-full first:rounded-l-full last:rounded-r-full" style={{ flexGrow: it.parts[k.key], minWidth: 2, background: k.color }} />
                ))}
              </span>
              <span className="tabular w-24 text-right text-ink">
                {fmt(it.value, data.format)}
                <span className="ml-1.5 text-[10px] text-ink-3">{total ? `${Math.round((it.value / total) * 100)}%` : ""}</span>
              </span>
              {hover === it.label && (
                <div role="tooltip" className="pointer-events-none absolute top-full left-[44%] z-10 mt-1 min-w-44 rounded-control border border-line bg-surface-raised px-2.5 py-2 text-[11px] shadow-pop">
                  <p className="mb-1 font-medium text-ink">{it.label}</p>
                  {breakup.map((k) => (
                    <p key={k.key} className="tabular flex items-center gap-1.5 whitespace-nowrap">
                      <span aria-hidden className="size-2 rounded-[2px]" style={{ background: k.color }} />
                      <span className="text-ink-2">{k.label}</span>
                      <span className="ml-auto pl-3 font-medium text-ink">{fmt(it.parts[k.key], data.format)}</span>
                      <span className="w-8 text-right text-ink-3">{share(it.parts[k.key])}</span>
                    </p>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Ranked horizontal bars (HTML, so long labels wrap gracefully). An item's `tone` colours its bar like its status pill. */
export function BarList({ data, max: maxRows = 15 }) {
  if (data.stack) return <StackedBarList data={data} max={maxRows} />;
  const items = data.items.slice(0, maxRows);
  const top = Math.max(1, ...items.map((i) => i.value));
  const total = data.items.reduce((s, i) => s + i.value, 0);
  if (!items.length) return <Empty />;
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it.label} className="group grid grid-cols-[minmax(0,44%)_1fr_auto] items-center gap-3 text-[12px]" title={`${it.label}: ${fmt(it.value, data.format)}${it.sub !== undefined ? ` · ${fmt(it.sub, data.subFormat ?? "int")}` : ""}`}>
          <span className="truncate text-ink">{it.label}</span>
          <span className="h-2 rounded-full bg-surface-2">
            <span
              className="block h-2 rounded-full transition-[width] group-hover:opacity-80"
              style={{ width: `${(it.value / top) * 100}%`, background: it.tone ? toneColor(it.tone) : "var(--series-1)" }}
            />
          </span>
          <span className="tabular w-24 text-right text-ink">
            {fmt(it.value, data.format)}
            {/* Share of total only makes sense for additive values; `share: false` turns it off (e.g. averages). */}
            {data.share !== false && <span className="ml-1.5 text-[10px] text-ink-3">{total ? `${Math.round((it.value / total) * 100)}%` : ""}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Donut with a legend table; folds beyond 6 slices into "Other". */
export function Donut({ data, size = 140 }) {
  const [hover, setHover] = useState(null);
  const slices = useMemo(() => {
    // `ordered` payloads keep their server order so an entity keeps its colour across charts.
    const sorted = data.ordered ? data.items : [...data.items].sort((a, b) => b.value - a.value);
    // `id` keeps slices distinct even when the data has its own "Other" label.
    const keyed = sorted.map((it, i) => ({ ...it, id: `${i}:${it.label}` }));
    if (keyed.length <= 6) return keyed;
    const rest = keyed.slice(5).reduce((s, i) => s + i.value, 0);
    return [...keyed.slice(0, 5), { label: "Other", value: rest, id: "__folded__", folded: true }];
  }, [data.items, data.ordered]);
  // Colours are fixed per slice before any are switched off, so a slice keeps its colour.
  const coloured = slices.map((sl, i) => ({ ...sl, color: sl.folded ? "var(--text-muted)" : seriesColor(i) }));
  const toggles = useToggles(coloured.map((sl) => sl.id));
  const shown = coloured.filter((sl) => !toggles.hidden.has(sl.id));
  const total = shown.reduce((s, i) => s + i.value, 0);
  if (!coloured.reduce((s, i) => s + i.value, 0)) return <Empty />;
  const r = size / 2;
  const inner = r * 0.62;
  const starts = shown.map((_, i) => -Math.PI / 2 + (shown.slice(0, i).reduce((acc, x) => acc + x.value, 0) / (total || 1)) * Math.PI * 2);
  const arcs = shown.map((s, i) => {
    const a = (s.value / (total || 1)) * Math.PI * 2;
    const start = starts[i];
    const angle = start + a;
    const large = a > Math.PI ? 1 : 0;
    const p = (rad, rr) => [r + rr * Math.cos(rad), r + rr * Math.sin(rad)];
    const [x1, y1] = p(start, r - 1);
    const [x2, y2] = p(angle, r - 1);
    const [x3, y3] = p(angle, inner);
    const [x4, y4] = p(start, inner);
    const d =
      shown.length === 1
        ? `M${r},1A${r - 1},${r - 1} 0 1 1 ${r - 0.01},1ZM${r},${r - inner}A${inner},${inner} 0 1 0 ${r + 0.01},${r - inner}Z`
        : `M${x1},${y1}A${r - 1},${r - 1} 0 ${large} 1 ${x2},${y2}L${x3},${y3}A${inner},${inner} 0 ${large} 0 ${x4},${y4}Z`;
    return { ...s, d };
  });
  const focus = hover !== null ? arcs[hover] : null;
  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg width={size} height={size} role="img" aria-label="Donut chart" className="shrink-0" onMouseLeave={() => setHover(null)}>
        {arcs.map((a, i) => (
          <path
            key={a.id}
            d={a.d}
            fill={a.color}
            fillRule="evenodd"
            stroke="var(--surface-1)"
            strokeWidth={2}
            opacity={hover === null || hover === i ? 1 : 0.45}
            onMouseEnter={() => setHover(i)}
          />
        ))}
        <text x={r} y={r - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
          {focus ? focus.label.slice(0, 16) : "Total"}
        </text>
        <text x={r} y={r + 12} textAnchor="middle" fontSize={15} fontWeight={600} fill="var(--text-primary)" className="tabular">
          {fmtCompact(focus ? focus.value : total)}
        </text>
      </svg>
      <ul className="min-w-40 flex-1 space-y-1 text-[12px]">
        {coloured.map((sl) => {
          const off = toggles.hidden.has(sl.id);
          const ai = arcs.findIndex((a) => a.id === sl.id);
          return (
            <li key={sl.id} className="flex items-center justify-between gap-3" onMouseEnter={() => setHover(ai < 0 ? null : ai)} onMouseLeave={() => setHover(null)}>
              {/* Click a row to take its slice out of the donut (shares are then of what is shown). */}
              <LegendItem label={sl.label} color={sl.color} off={off} onToggle={() => toggles.toggle(sl.id)} className={`min-w-0 ${off ? "" : "text-ink-2"}`}>
                <span className="truncate">{sl.label}</span>
              </LegendItem>
              <span className={`tabular ${off ? "text-ink-3" : "text-ink"}`}>
                {fmt(sl.value, data.format)}{" "}
                {!off && <span className="text-[10px] text-ink-3">{((sl.value / (total || 1)) * 100).toFixed(1)}%</span>}
              </span>
            </li>
          );
        })}
        {coloured.length > 1 && <ShowAll toggles={toggles} />}
      </ul>
    </div>
  );
}

/** Funnel: ordinal blue ramp, conversion to the previous step and to the first. */
/**
 * Funnel. A step's percentage is of its `parent` step (default: the one before),
 * named in the label, so it is never read against the wrong population. A
 * `branch` step is a side split of its parent (e.g. web-only), drawn indented
 * and lighter: it is not a stage the next step passes through.
 */
export function Funnel({ data }) {
  const first = data.steps[0]?.value || 0;
  const ramp = ["var(--seq-700)", "var(--seq-550)", "var(--seq-400)", "var(--seq-250)"];
  return (
    <ol className="space-y-3">
      {data.steps.map((s, i) => {
        const pi = s.parent ?? (i ? i - 1 : null);
        const parent = pi !== null ? data.steps[pi] : null;
        return (
          <li key={s.label} className={s.branch ? "ml-5 border-l-2 border-dashed border-line-default pl-3" : ""}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-[12px]">
              <span className={s.branch ? "text-ink-3" : "text-ink-2"}>{s.label}</span>
              <span className="tabular font-medium text-ink">
                {fmt(s.value)}
                {parent && (
                  <span className="ml-2 text-[10px] font-normal text-ink-3">{parent.value ? `${((s.value / parent.value) * 100).toFixed(1)}% of “${parent.label.toLowerCase()}”` : ""}</span>
                )}
              </span>
            </div>
            <div className="h-4 rounded-[3px] bg-surface-2">
              <div
                className="h-4 rounded-[3px]"
                style={{
                  width: `${first ? Math.max(0.5, (s.value / first) * 100) : 0}%`,
                  background: s.branch ? "var(--seq-250)" : ramp[Math.min(i, ramp.length - 1)],
                  opacity: s.branch ? 0.7 : 1,
                }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Weekday × hour heatmap on the sequential blue ramp (quantile steps). */
export function Heatmap({ data }) {
  const [hover, setHover] = useState(null);
  const flat = data.values.flat().filter((v) => v > 0).sort((a, b) => a - b);
  const q = (p) => flat[Math.min(flat.length - 1, Math.floor(p * flat.length))] ?? 0;
  const cuts = [q(0.2), q(0.4), q(0.6), q(0.8)];
  const steps = ["var(--heat-1)", "var(--heat-2)", "var(--heat-3)", "var(--heat-4)", "var(--heat-5)"];
  const color = (v) => (v <= 0 ? "var(--surface-2)" : steps[cuts.filter((c) => v > c).length]);
  const wrap = useRef(null);
  const enter = (e, r, c, v) => {
    // Tooltip anchored above the hovered cell, in the scroll container's coordinates.
    const box = wrap.current.getBoundingClientRect();
    const cell = e.currentTarget.getBoundingClientRect();
    setHover({ r, c, v, x: cell.left - box.left + wrap.current.scrollLeft + cell.width / 2, y: cell.top - box.top });
  };
  return (
    <div ref={wrap} className="relative overflow-x-auto scroll-thin">
      {/* Fixed layout: every hour gets the same width (empty cells would otherwise collapse to 0). */}
      <table className="w-full min-w-[560px] table-fixed border-separate" style={{ borderSpacing: 2 }}>
        <colgroup>
          <col style={{ width: 40 }} />
          {data.cols.map((h) => (
            <col key={h} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th />
            {data.cols.map((h) => (
              <th key={h} className="text-center text-[10px] font-normal text-ink-3">
                {h % 3 === 0 ? fmtAxis(h, "hour") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((day, ri) => (
            <tr key={day}>
              <th className="pr-2 text-right text-[10px] font-normal text-ink-3">{day}</th>
              {data.values[ri].map((v, ci) => (
                <td
                  key={ci}
                  className="h-5 rounded-[2px]"
                  style={{ background: color(v), outline: hover && hover.r === ri && hover.c === ci ? "2px solid var(--text-primary)" : "none" }}
                  onMouseEnter={(e) => enter(e, ri, ci, v)}
                  onMouseLeave={() => setHover(null)}
                  aria-label={`${day} ${ci}:00 — ${fmt(v, data.format)}`}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hover && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-control border border-line bg-surface-raised px-2.5 py-1.5 text-[11px] shadow-pop"
          style={{ left: hover.x, top: hover.y - 4 }}
        >
          <span className="text-ink-3">
            {data.rows[hover.r]} {fmtX(hover.c, "hour")}
          </span>{" "}
          <span className="font-medium text-ink tabular-nums">{fmt(hover.v, data.format)}</span>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-ink-3">
        <span>{hover ? `${data.rows[hover.r]} ${fmtX(hover.c, "hour")}: ${fmt(hover.v, data.format)}` : "Hover a cell for its value"}</span>
        <span className="flex items-center gap-1">
          Less
          {steps.map((s) => (
            <span key={s} className="inline-block h-3 w-4 rounded-[2px]" style={{ background: s }} />
          ))}
          More
        </span>
      </div>
    </div>
  );
}

/** Segment shades of one hue: the rows of a tile mean different things, so they share no categorical colours. */
const PART_SHADES = [1, 0.55, 0.3];

/**
 * How a KPI's total breaks down: the web/app split, then its `parts`. Each row
 * is a thin share bar (segments in proportion, 2px gaps) over a legend of
 * label, value and share. A label never wraps away from its value.
 * Collapsed until asked for: the headline number is what most readers need.
 */
function Breakdowns({ k }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const rows = [
    ...(k.split ? [{ title: "Platform", items: [{ label: "Web", value: k.split.web }, { label: "App", value: k.split.app }] }] : []),
    ...(k.parts ?? []),
  ];
  if (!rows.length) return null;
  // Shares only for counts; a split of percentages or durations is not additive.
  const counts = k.format === "int";
  return (
    <div className="mt-3 border-t border-line pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="-mx-1 flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-ink-3 hover:text-ink-2 focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)]"
      >
        {open ? "Hide breakdown" : `Breakdown by ${rows.map((r) => r.title.toLowerCase()).join(", ")}`}
        <Icon name="chevronDown" className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <BreakdownRows id={id} rows={rows} k={k} counts={counts} />}
    </div>
  );
}

function BreakdownRows({ id, rows, k, counts }) {
  return (
    <div id={id} className="tabular mt-2 flex flex-col gap-3 text-[11.5px]">
      {rows.map((row) => {
        const total = row.items.reduce((s, p) => s + (Number(p.value) || 0), 0);
        const pct = (v) => `${Math.round(((Number(v) || 0) / total) * 100)}%`;
        const shade = (i) => ({ background: "var(--series-1)", opacity: PART_SHADES[i] ?? 0.3 });
        return (
          <section key={row.title} aria-label={`${row.title}: ${row.items.map((p) => `${p.label} ${fmt(p.value, k.format)}`).join(", ")}`}>
            <h4 className="mb-1.5 text-[10px] font-semibold tracking-[0.08em] text-ink-3 uppercase">{row.title}</h4>
            {counts && total > 0 && (
              <div aria-hidden className="mb-1.5 flex h-1.5 gap-[2px] overflow-hidden rounded-full">
                {row.items
                  .filter((p) => Number(p.value) > 0)
                  .map((p) => (
                    <span key={p.label} className="h-full first:rounded-l-full last:rounded-r-full" style={{ ...shade(row.items.indexOf(p)), flexGrow: Number(p.value), minWidth: 3 }} title={`${p.label}: ${fmt(p.value, k.format)} (${pct(p.value)})`} />
                  ))}
              </div>
            )}
            <ul className="flex flex-wrap gap-x-3.5 gap-y-1">
              {row.items.map((p, i) => (
                <li key={p.label} className="flex items-center gap-1.5 whitespace-nowrap">
                  {counts && <span aria-hidden className="size-1.5 rounded-full" style={shade(i)} />}
                  <span className="text-ink-2">{p.label}</span>
                  <span className="font-semibold text-ink">{fmt(p.value, k.format)}</span>
                  {counts && total > 0 && <span className="text-[10.5px] text-ink-3">{pct(p.value)}</span>}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

/** One KPI: label, big value with its delta, optional breakdowns and hint. */
function Kpi({ k, basis, size }) {
  const missing = k.state === "not_measured" || k.state === "not_applicable";
  return (
    <>
      <p className="flex items-start gap-1.5 text-[13px] leading-snug font-medium text-ink-2">
        {/* Names say what is counted, so they may need two lines rather than an ellipsis. */}
        <span className="line-clamp-2 min-w-0">{k.label}</span>
        {k.state === "proxy" && (
          <span className="shrink-0 rounded px-1 text-[10px] font-semibold" style={{ color: "var(--status-warning)", background: "var(--status-warning-bg)" }} title={MEASURE_HINTS.proxy}>
            Proxy
          </span>
        )}
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {missing ? (
          // Said in words, in the value's place: a missing signal is not zero.
          <span className="mt-1.5 rounded px-1.5 py-0.5 text-[13px] font-semibold" style={{ color: "var(--status-neutral)", background: "var(--status-neutral-bg)" }} title={MEASURE_HINTS[k.state]}>
            {MEASURE_LABELS[k.state]}
          </span>
        ) : (
          <span className={`tabular leading-none font-semibold tracking-tight text-ink ${size}`}>{fmt(k.value, k.format)}</span>
        )}
        {!missing && k.prev !== undefined && <DeltaBadge current={k.value} previous={k.prev} basis={basis} />}
        {!missing && k.prev > 0 && <span className="text-[11.5px] text-ink-3">vs previous period</span>}
      </div>
      <Breakdowns k={k} />
      {k.hint && <p className="mt-1.5 line-clamp-2 text-[11px] leading-snug text-ink-3">{k.hint}</p>}
      {k.link && <KpiLink link={k.link} />}
    </>
  );
}

/** Where to act on a number (mockup "Review work →"); the page's filters carry over. */
function KpiLink({ link }) {
  const { search } = useLocation();
  return (
    <Link to={{ pathname: link.to, search }} className="mt-2 inline-block text-[11.5px] font-medium text-accent-ink underline-offset-2 hover:underline">
      {link.label} →
    </Link>
  );
}

/**
 * KPI strip. `tiles` (default): one card per KPI, as in the mockup; an item
 * with `attention: true` gets an amber top edge (a number that asks for action).
 * `panel`: the KPIs side by side inside one card (the caller draws the card).
 */
export function KpiRow({ data, basis, layout = "tiles" }) {
  const n = data.items.length;
  const cols = n >= 6 ? "xl:grid-cols-6" : n === 5 ? "xl:grid-cols-5" : n === 4 ? "xl:grid-cols-4" : n === 2 ? "xl:grid-cols-2" : "xl:grid-cols-3";
  if (layout === "panel") {
    // A panel sits in a half-width card: at most three numbers per row.
    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        {data.items.map((k) => (
          <div key={k.label} className="min-w-0" title={k.hint || undefined}>
            <Kpi k={k} basis={basis} size="text-[24px]" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={`grid grid-cols-2 gap-4 md:grid-cols-3 ${cols}`}>
      {data.items.map((k) => (
        <div
          key={k.label}
          className="relative min-w-0 overflow-hidden rounded-card bg-surface-1 p-5 shadow-card transition-shadow duration-200 hover:shadow-card-hover"
          title={k.hint || undefined}
        >
          {/* A number that asks for action: an amber top edge. */}
          {k.attention && <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ background: "var(--attention)" }} />}
          <Kpi k={k} basis={basis} size="text-[30px]" />
        </div>
      ))}
    </div>
  );
}

/**
 * An empty chart or list. With filters set it is "No matching items" and offers
 * Clear filters; otherwise it is an observed zero, not missing data (a missing
 * source shows "Not measured" or "Source unavailable" instead).
 */
export function Empty({ text }) {
  return <EmptyResult text={text} height="h-28" />;
}

export function EmptyResult({ text, height = "h-24" }) {
  const { filtered, clear } = useEmptyContext();
  if (text) return <div className={`flex ${height} items-center justify-center text-[12px] text-ink-3`}>{text}</div>;
  return (
    <div className={`flex ${height} flex-col items-center justify-center gap-1 px-4 text-center`}>
      <p className="text-[12.5px] font-medium text-ink">{filtered ? "No matching items" : "Zero observed"}</p>
      <p className="max-w-sm text-[11.5px] text-ink-3">
        {filtered ? "The filters set leave nothing to show here. This is not a performance score." : "No recorded activity for this selection: an observed zero for the sources in Scope, not missing data."}
      </p>
      {filtered && clear && (
        <Button size="xs" onClick={clear} className="mt-1">
          Clear filters
        </Button>
      )}
    </div>
  );
}

